import React, { type FC, useEffect, useMemo, useState } from 'react';
import { type FormikErrors, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { Button } from 'components/Shared/Inputs';
import { SectionContainer } from '../../Other/SectionContainer';
import { LANGUAGES_REF_DATA, UPDATE_SERVED_LANGUAGES_MUTATION } from './queries';
import { useLazyQuery, useMutation } from '@apollo/client';
import RemoveIcon from '@mui/icons-material/HighlightOffRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import { PickerDialog, type Option } from '../../Other/PickerDialog';
import { type Language } from '../../../interfaces';
import { Spinner } from 'components/Shared/Spinner';
import { confirm } from 'components/Shared/ConfirmDialog';

interface LanguagesServedProps {
    providerId: number,
    languages?: Language[],
    onUpdated: (languages?: Language[]) => void
}

interface ServedLanguagesFormValues {
    languageCodes: string[]
}


const LanguagesComparator = (A: Option, B: Option) => {
    if (A.title < B.title) {
        return -1;
    } else if (A.title > B.title) {
        return 1;
    } else {
        return 0;
    }
}

export const LanguagesServed: FC<LanguagesServedProps> = (props) => {
    const { providerId, languages, onUpdated } = props;

    const { t, ready: translationsReady } = useTranslation('providerprofile', { useSuspense: false });
    const { t: t_common, ready: commonTranslationsReady } = useTranslation('common', { useSuspense: false });

    const [cachePendingUpdate, setCachePendingUpdate] = useState(false);

    const [updateServedLanguages, updateServedLanguagesMutation] = useMutation(UPDATE_SERVED_LANGUAGES_MUTATION, {
        update: (_, result) => {
            if (result.data?.updateProviderDetails?.details?.servedLanguages) {
                setCachePendingUpdate(true);
                onUpdated(result.data.updateProviderDetails.details.servedLanguages)
            }
        }
    });

    const [getRefData, refDataQuery] = useLazyQuery(LANGUAGES_REF_DATA);

    const [inEdit, setInEdit] = useState(false);

    useEffect(() => {
        setCachePendingUpdate(false);
        setInEdit(false);
        formik.resetForm({ values: { languageCodes: languages?.map(language => language.value) ?? [] }});
    }, [languages]);

    useEffect(() => {
        if (inEdit && !refDataQuery.called) {
            getRefData();
        }
    }, [inEdit]);

    const formik = useFormik<ServedLanguagesFormValues>({
        initialValues: {
            languageCodes: languages?.map(language => language.value) ?? []
        },
        validate: (values) => {
            const errors: FormikErrors<ServedLanguagesFormValues> = {};

            if (values.languageCodes.length < 1) {
                errors.languageCodes = "Select at least one language";
            }

            return errors;
        },
        onSubmit: async (values) => {
            const dialogProps = {
                message: <>
                    <p>{t('common__confirm_changes_dialog_text')}</p>
                    {
                        Boolean(addedEntries.length) &&
                        <>
                            {t('served_languages__attestation_added_entries_header')}
                            <ul>
                                {
                                    addedEntries.map(entry => (
                                        <li key={entry.value}>{entry.title}</li>
                                    ))
                                }
                            </ul>
                        </>
                    }
                    {
                        Boolean(removedEntries.length) &&
                        <>
                            {t('served_languages__attestation_removed_entries_header')}
                            <ul>
                                {
                                    removedEntries.map(entry => (
                                        <li key={entry.value}>{entry.title}</li>
                                    ))
                                }
                            </ul>
                        </>
                    }
                </>
            }
            if (await confirm(dialogProps)) {
                updateServedLanguages({
                    variables: {
                        providerId: providerId,
                        languageCodes: values.languageCodes
                    }
                });
            }
        }
    });

    const options = refDataQuery.data?.languages;
    const value = options ? formik.values.languageCodes.map(code => options.find(l => l.value === code)!) : undefined;
    
    const addedEntries = useMemo(() => (
        value?.filter(newEntry => !languages?.find(oldEntry => oldEntry.value === newEntry.value)) ?? []
    ), [value, languages]);
    
    const removedEntries = useMemo(() => (
        languages?.filter(oldEntry => !value?.find(newEntry => oldEntry.value === newEntry.value.toString())) ?? []
    ), [value, languages]);

    const handleCancelChanges = async () => {
        if (addedEntries.length || removedEntries.length) {
            const dialogProps = {
                title: t('common__discard_changes_dialog_title'),
                message: t('common__discard_changes_dialog_text')
            };
            if (await confirm(dialogProps)) {
                formik.resetForm();
                setInEdit(false);
            }
        } else {
            formik.resetForm();
            setInEdit(false);
        }
    }

    const handleRemoveLanguageClick = (languageCode) => {
        formik.setFieldValue('languageCodes', formik.values.languageCodes?.filter(code => code !== languageCode));
    }

    const [showPickerDialog, setShowPickerDialog] = useState(false);

    const handleAddLanguages = (languages) => {
        const updatedLanguages = [...(value ?? []), ...languages].sort(LanguagesComparator);
        formik.setFieldValue('languageCodes', updatedLanguages.map(lang => lang.value));
        setShowPickerDialog(false);
    }

    const editorLoading = refDataQuery.loading || !commonTranslationsReady;

    const saveDisabled = updateServedLanguagesMutation.loading
        || (!addedEntries.length && !removedEntries.length)
        || (value?.length ?? 0) < 1
        || cachePendingUpdate;

    return !translationsReady
        ? <></>
        : <SectionContainer title={t('served_languages__section_title')} inEdit={inEdit} onEditClick={() => setInEdit(true)}>
            {
                !inEdit
                    ? languages?.length
                        ? <>
                            <p>{t('served_languages__list_header')}</p>
                            <ul className="insurance_panels_list">
                                {
                                    languages.map(language => (
                                        <li key={language.value}><span>{language.title}</span></li>
                                    ))
                                }
                            </ul>
                        </>
                        : <p>{t('served_languages__no_entries')}</p>
                    : <>
                        {
                            editorLoading &&
                            <Spinner fillParent/>
                        }
                        {
                            (updateServedLanguagesMutation.loading || cachePendingUpdate) &&
                            <Spinner fillParent backdrop/>
                        }
                        <Grid container component="form" onSubmit={formik.handleSubmit} spacing={3} style={{visibility: editorLoading ? 'hidden' : 'visible'}}>
                            <Grid item xs={12} component="p">
                                {t('served_languages__list_header')}
                            </Grid>
                            <Grid item xs={12} component="ul" className="insurance_panels_edit_list">
                                {
                                    value?.map(language => (
                                        <li key={language.value}>
                                            <IconButton size="small" onClick={() => handleRemoveLanguageClick(language.value)}><RemoveIcon/></IconButton>
                                            {language.title}
                                        </li>
                                    ))
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant="text" onClick={() => setShowPickerDialog(true)}><AddIcon/>{t('served_languages__add_entry_button_label')}</Button>
                            </Grid>
                            <Grid item xs={12} container spacing={2}>
                                <Grid item>
                                    <Button type="submit" variant="opaque" disabled={saveDisabled}>
                                        {t_common('save')}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button type="button" variant="text" onClick={handleCancelChanges}>{t_common('cancel')}</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <PickerDialog open={showPickerDialog}
                            options={options}
                            value={value}
                            onCancel={() => setShowPickerDialog(false)}
                            labels={{
                                dialogTitle: t('served_languages__picker_dialog_title'),
                                helperText: t('served_languages__picker_dialog_helper_text'),
                                inputPlaceholder: t('served_languages__picker_dialog_input_placeholder')
                            }}
                            onSubmit={handleAddLanguages}
                        />
                    </>
            }
        </SectionContainer>
}